import styled from '@emotion/styled';
import { Avatar, Button, Card as _Card, CardHeader as _CardHeader, Grid, Typography } from '@material-ui/core';
import _QRCode from 'qrcode.react';
import { ClassAttributes, createElement, Fragment, useCallback, useContext, useState } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';

import { Creation, Deliverable, File, Maybe } from '../../../../graphql/generated/graphql';
import { toJSDate } from '../../../../utility/graphql/datetime';
import MediaPlayer from '../../../component/compound/MediaPlayer';
import AspectRatioFixed from '../../../component/primitive/AspectRatioFixed';
import { SPACING_UNIT } from '../../../constant/spacing';
import { context as ConfigContext } from '../../../context/config';
import { IntentDialog } from './IntentDialog';

type Props = ClassAttributes<HTMLElement> & {
  creation: Pick<Creation, 'id' | 'assignee'> & {
    latestProposedDeliverable?:
      | (Pick<Deliverable, 'createdAt' | 'intent'> & {
          creative: {
            file: Pick<File, 'url' | 'mimeType'>;
          };
          attachedThumbnail?: Maybe<{
            file: Pick<File, 'url'>;
          }>;
        })
      | null;
  };
  className?: string;
};

const CreationsItem = ({ creation, className }: Props) => {
  const config = useContext(ConfigContext);
  const profileWebUrl = config.PROFILE_WEB_URL;

  const dUrl = creation.latestProposedDeliverable ? creation.latestProposedDeliverable.creative.file.url : '';

  const [openModal, setOpenModal] = useState(false);

  const handleOpen = useCallback(() => {
    setOpenModal(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpenModal(false);
  }, []);

  const onClickOptimizer = useCallback((url: string) => {
    const win = window.open(url, '_blank');
    if (win !== null) {
      win.focus();
    }
  }, []);

  const createdAt = (
    <CreatedAt>
      <Typography variant="caption" color="textSecondary">
        <FormattedDate
          year="numeric"
          month="2-digit"
          day="2-digit"
          hour="2-digit"
          minute="2-digit"
          value={toJSDate(creation.latestProposedDeliverable!.createdAt)}
        >
          {(date: string) => <FormattedMessage id="creations.creations_item.created_at" values={{ date }} />}
        </FormattedDate>
      </Typography>
    </CreatedAt>
  );

  return (
    <Fragment>
      <Card className={className}>
        <CardHeader
          avatar={
            <Avatar
              src={creation.assignee!.profileImageUrl}
              alt={creation.assignee!.name}
              onClick={() => onClickOptimizer(`${profileWebUrl}/users/${creation.assignee!.id}`)}
            />
          }
          title={<Name>{creation.assignee!.name}</Name>}
          subheader={createdAt}
        />
        <VideoOrImage aspectRatio={[1, 1]}>
          <MediaPlayer
            mimeType={creation.latestProposedDeliverable!.creative.file.mimeType}
            url={creation.latestProposedDeliverable!.creative.file.url}
          />
        </VideoOrImage>
        <FooterGrid container>
          <Grid container item xs={8} justify={'flex-start'}>
            <Grid item>
              <IntentButton variant="outlined" color="primary" onClick={handleOpen}>
                <FormattedMessage id="creations.creations_item.detail_button" />
              </IntentButton>
            </Grid>
          </Grid>
          <Grid container item xs={4} justify={'flex-end'}>
            <Grid item>
              {dUrl ? (
                <QRCode
                  value={dUrl && dUrl.slice(0, 2) === '//' ? `${window.location.protocol}${dUrl}` : dUrl}
                  size={75}
                  level="L"
                  renderAs="svg"
                />
              ) : null}
            </Grid>
          </Grid>
        </FooterGrid>
      </Card>
      <IntentDialog
        open={openModal}
        onRequestClose={handleClose}
        assignee={{
          profileImageUrl: creation.assignee!.profileImageUrl,
          name: creation.assignee!.name,
        }}
        deliverable={creation.latestProposedDeliverable!}
        key={creation.id}
      />
    </Fragment>
  );
};

const QRCode = styled(_QRCode)({});

const Card = styled(_Card)({});

const CardHeader = styled(_CardHeader)({
  width: '100%',
});

const Name = styled('div')({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: '192px',
  minWidth: '80%',
});

const FooterGrid = styled(Grid)({
  padding: SPACING_UNIT,
});

const CreatedAt = styled(Typography)({});

const VideoOrImage = styled(AspectRatioFixed)({});

const IntentButton = styled(Button)({
  margin: SPACING_UNIT,
});

export default CreationsItem;
